import type {ToastMessageService} from 'shared/components/kp-toast-messages';
import type {ScannerFactory} from './barcode-scanner.types';
import type {ScannerState} from './barcode-scanner.types';
import type {Barcode} from './barcode';
import type {StreamBarcodeScanner} from './stream-barcode-scanner';
import type {Observable} from 'rxjs';
import {BehaviorSubject, Subject} from 'rxjs';
import {tap} from 'rxjs/operators';

export class BarcodeScannerService {

    public static readonly serviceName = 'barcodeScannerService';

    private scannerEnabledByUser: boolean;
    private readonly scannerEnabledGlobally: boolean;

    private scannerViewerReady: boolean;
    private scannerActive: boolean;

    private barcodeScanner: StreamBarcodeScanner;

    private readonly scannerViewerDisplayed$: BehaviorSubject<boolean>;
    private readonly scannerValue$: Subject<Barcode>; // stream of values from scanner (source)
    private readonly scannedBarcode$: Observable<Barcode>; // stream with automatic camera control, output to service subscribers
    private readonly scannerEnabledState$: Subject<boolean>
    private deferActivationUntilViewerReady: boolean;
    private deferOpeningUntilViewerReady: boolean;

    private scannerDeactivationTimeoutId: number = null;

    private barcodeConsumersCount = 0;

    public readonly SCANNER_DEACTIVATION_TIMEOUT_MS = 10000;
    private readonly SCANNER_ACTIVATION_ERROR_MSG = 'Scanner activation error';
    private readonly BARCODE_CONSUMER_MISSING_MSG = 'No barcode consumer to unregister';
    private readonly VIEWER_COMPONENT_MISSING_MSG = 'Scanner Viewer Component is not registered!';

    /*@ngInject*/
    constructor(CAMERA_SCANNER_ENABLED: boolean,
                private zxingJsBarcodeScannerFactory: ScannerFactory,
                private toastMessageService: ToastMessageService) {

        this.scannerViewerReady = false;
        this.deferActivationUntilViewerReady = false;
        this.deferOpeningUntilViewerReady = false;
        this.scannerViewerDisplayed$ = new BehaviorSubject<boolean>(false);

        this.scannerEnabledGlobally = CAMERA_SCANNER_ENABLED;
        this.scannerEnabledByUser = false;

        this.scannerEnabledState$ = new BehaviorSubject<boolean>(this.scannerEnabledByUser);

        this.scannerValue$ = new Subject<Barcode>();
        this.scannedBarcode$ = this.scannerValue$.pipe(
            tap({
                subscribe: () => this.onScannedBarcode$Subscribe(),
                unsubscribe: () => this.onScannedBarcode$Unsubscribe()
            })
        );
    }

    private get isScannerEnabledByUser(): boolean {
        return this.scannerEnabledByUser;
    }

    private set isScannerEnabledByUser(value: boolean) {

        if (value === this.scannerEnabledByUser) { // do nothing if set to current value
            return;
        }

        this.scannerEnabledByUser = value;
        this.scannerEnabledState$.next(value);

        if (!this.isGloballyEnabled()) { // ignore if scanner is globally disabled
            return;
        }

        if (value && !this.scannerActive) { // start or stop scanning
            if (this.scannerViewerReady) {
                this.activateScanner(); // activate immediately
            } else {
                this.deferActivationUntilViewerReady = true; // defer activation until viewer is registered
            }
        }
    }

    public getScannerEnabledState$(): Observable<boolean> {
        return this.scannerEnabledState$.asObservable();
    }

    public toggleScanning() {
        if (this.isScannerEnabledByUser) {
            this.stopScanning();
        } else {
            this.startScanning();
        }
    }

    public startScanning() {
        this.isScannerEnabledByUser = true;
        if (!!this.barcodeScanner) { // scanner could be not yet initialized
            this.barcodeScanner.clearValidationBuffer();
        }
    }

    public stopScanning() {
        this.isScannerEnabledByUser = false;
    }

    public getScannerState$(): Observable<ScannerState> {
        if (!this.scannerViewerReady) {
            throw new Error(this.VIEWER_COMPONENT_MISSING_MSG);
        }
        return this.barcodeScanner.getState$();
    }

    public isAllowed(): boolean {
        return this.isGloballyEnabled() && this.isScannerEnabledByUser;
    }

    public isGloballyEnabled(): boolean {
        return this.scannerEnabledGlobally;
    }

    public async isUsable(): Promise<boolean> {
        if (!this.isGloballyEnabled() || !this.isSupportedByBrowser()) {
            return false;
        }
        return this.isCameraDeviceAvailable();
    }

    // register barcode scanner directive
    public registerBarcodeScannerConsumer(): void {

        this.barcodeConsumersCount++;

        if (!this.isAllowed() || this.scannerActive) { // ignore if already activated or not allowed at all
            return;
        }

        if (this.scannerViewerReady) {
            this.activateScanner(); // activate immediately
        } else {
            this.deferActivationUntilViewerReady = true; // defer activation until viewer is registered
        }
    }

    // unregister barcode scanner directive
    public unregisterBarcodeScannerConsumer(): void {

        if (this.barcodeConsumersCount === 0) {
            throw new Error(this.BARCODE_CONSUMER_MISSING_MSG);
        }

        this.barcodeConsumersCount--;

        if (this.barcodeConsumersCount !== 0) {
            return;
        }

        if (this.scannerViewerReady) {
            this.closeScanner();
        } else {
            this.deferActivationUntilViewerReady = false; // cancel deferred activation
        }
    }

    public getBarcodeScannerConsumersCount(): number {
        return this.barcodeConsumersCount;
    }

    public isSupportedByBrowser(): boolean {
        const mediaDevices = window.navigator.mediaDevices;
        return mediaDevices && typeof mediaDevices.getUserMedia === 'function';
    }

    public async isCameraDeviceAvailable(): Promise<boolean> {
        const devices = await window.navigator.mediaDevices.enumerateDevices();
        return devices.some((device) => device.kind === 'videoinput');
    }

    public getScannedBarcode$(): Observable<Barcode> {
        return this.scannedBarcode$;
    }

    public async registerScannerViewer(videoElementRef: HTMLVideoElement): Promise<Observable<boolean>> {


        this.barcodeScanner = await this.zxingJsBarcodeScannerFactory.createScanner(videoElementRef);

        this.scannerViewerReady = true;

        if (this.deferActivationUntilViewerReady) { // deferred activation
            this.activateScanner();
        }

        if (this.deferOpeningUntilViewerReady) { // deferred opening
            this.openViewer();
        }

        return this.scannerViewerDisplayed$.asObservable();
    }

    public unregisterScannerViewer() {
        this.deferActivationUntilViewerReady = false;
        this.deferOpeningUntilViewerReady = false;
        this.closeScanner();
        this.scannerViewerReady = false;
        this.barcodeScanner = null;
    }

    public isScannerViewerDisplayed(): boolean {
        return this.scannerViewerDisplayed$.value;
    }

    // start scanning
    private activateScanner() {

        if (!this.scannerViewerReady) {
            throw new Error(this.VIEWER_COMPONENT_MISSING_MSG)
        }

        this.scannerActive = true;

        // split subscription chain in order to reuse one subject
        const barcode$ = this.barcodeScanner.scan();
        barcode$.subscribe({
            next: (barcode) => this.onScannerValue(barcode),
            error: (error) => this.onScannerError(error)
        });
    }

    // stop scanning
    private deactivateScanner() {

        this.cancelScannerDeactivationTimeout();

        if (!this.scannerViewerReady) {
            throw new Error(this.VIEWER_COMPONENT_MISSING_MSG)
        }

        this.barcodeScanner.stopScanning();
        this.scannerActive = false;
    }

    private onScannerValue(barcode: Barcode) {
        if (this.isScannerEnabledByUser) {
            this.scannerValue$.next(barcode);
        }
    }

    private onScannerError(error: Error) {
        this.toastMessageService.showError(error.message, error.name ?? this.SCANNER_ACTIVATION_ERROR_MSG);
        this.closeScanner();
    }

    private onScannedBarcode$Subscribe() {
        if (!this.isScannerEnabledByUser) {
            return;
        }
        if (this.scannerViewerReady) {
            this.openViewer();
        } else {
            this.deferOpeningUntilViewerReady = true; // defer opening until viewer is registered
        }
    }

    private onScannedBarcode$Unsubscribe() {

        if (this.scannerViewerReady) {
            this.closeViewer();
        } else {
            this.deferOpeningUntilViewerReady = false; // cancel deferred opening
        }
    }

    private openViewer() {

        if (this.scannerViewerDisplayed$.value)
            return;

        this.cancelScannerDeactivationTimeout();
        this.scannerViewerDisplayed$.next(true);
    }

    private closeViewer() {

        if (!this.scannerViewerDisplayed$.value)
            return;

        this.scannerViewerDisplayed$.next(false);
        this.startScannerDeactivationTimeout();
    }

    private closeScanner() {
        this.stopScanning();
        this.deactivateScanner();
    }

    private startScannerDeactivationTimeout() {
        this.scannerDeactivationTimeoutId = window.setTimeout(() => this.closeScanner(), this.SCANNER_DEACTIVATION_TIMEOUT_MS);
    }

    private cancelScannerDeactivationTimeout() {

        if (this.scannerDeactivationTimeoutId !== null) {
            window.clearTimeout(this.scannerDeactivationTimeoutId);
            this.scannerDeactivationTimeoutId = null;
        }
    }
}